import React, { useState, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import createDecorator from 'final-form-focus';

import {
  Button,
  Modal,
  ModalFooter,
  Loading,
  Callout,
} from '@folio/stripes/components';

import FormFieldsGenerator from '../../../../../../../shared/FormFieldsGenerator';

// Create focus decorator for automatic focus on validation errors
const focusOnError = createDecorator();

const ItemEditModal = ({
  isOpen,
  itemIndex,
  fields,
  template,
  controlledVocab,
  holdingsRecordId,
  onSave,
  onCancel,
  onClose,
  isLoading,
  validationErrors,
}) => {
  const formRef = useRef();

  // Get current item data
  const currentItem = useMemo(() => {
    if (itemIndex !== null && fields?.value?.[itemIndex]) {
      return fields.value[itemIndex];
    }
    return null;
  }, [itemIndex, fields?.value]);

  // Generate item title for header
  const itemTitle = useMemo(() => {
    if (!currentItem) return '';

    const copyNumber = currentItem.copyNumber || itemIndex + 1;
    const barcode = currentItem.barcode || '';
    const volume = currentItem.volume || '';

    return { copyNumber, volume, barcode };
  }, [currentItem, itemIndex]);

  // Handle form submission with validation
  const onHandleSubmit = useCallback((values) => {
    // Update the item in the fields array
    if (fields && itemIndex !== null) {
      fields.update(itemIndex, values);
    }

    if (onSave) {
      onSave(itemIndex);
    }
    onClose();
  }, [fields, itemIndex, onSave, onClose]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    }
    onClose();
  }, [onCancel, onClose]);

  // Render footer
  const renderFooter = (handleSubmit, pristine, submitting) => {
    return (
      <ModalFooter>
        <Button
          buttonStyle="primary"
          type="submit"
          onClick={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
          disabled={submitting}
        >
          <FormattedMessage
            id="ui-plugin-inventory-templates.itemEditor.modal.saveButton"
            defaultMessage="Save Changes"
          />
        </Button>
        <Button
          buttonStyle="default"
          onClick={handleCancel}
          disabled={submitting}
        >
          <FormattedMessage
            id="ui-plugin-inventory-templates.itemEditor.modal.cancelButton"
            defaultMessage="Cancel"
          />
        </Button>
      </ModalFooter>
    );
  };

  // Render nothing if not open or no item
  if (!isOpen || itemIndex === null || !currentItem || !holdingsRecordId) {
    return null;
  }

  return (
    <Form
      onSubmit={onHandleSubmit}
      mutators={{ ...arrayMutators }}
      decorators={[focusOnError]}
      initialValues={currentItem}
      render={({ handleSubmit, pristine, submitting, form }) => {
        // Add form ref for external access
        formRef.current = form;

        return (
          <Modal
            open={isOpen}
            onClose={onClose}
            footer={renderFooter(handleSubmit, pristine, submitting)}
            wrappingElement="form"
            label={
              <div>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.itemEditor.modal.title"
                  defaultMessage="Edit Item"
                />
                {itemTitle && itemTitle.copyNumber && (
                  <>
                    {' - '}
                    <FormattedMessage
                      id="ui-plugin-inventory-templates.itemEditor.modal.copyNumber"
                      defaultMessage="Copy {number}"
                      values={{ number: itemTitle.copyNumber }}
                    />
                    {itemTitle.volume && (
                      <>
                        {' - '}
                        <FormattedMessage
                          id="ui-plugin-inventory-templates.itemEditor.modal.volume"
                          defaultMessage="Volume {volume}"
                          values={{ volume: itemTitle.volume }}
                        />
                      </>
                    )}
                    {itemTitle.barcode && ` [${itemTitle.barcode}]`}
                  </>
                )}
              </div>
            }
            size="large"
            dismissible
          >
            <form onSubmit={handleSubmit}>
              <div style={{ padding: '16px' }}>
                <div
                  style={{
                    marginBottom: '16px',
                    padding: '12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    fontSize: '14px',
                    color: '#6c757d',
                  }}
                >
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.itemEditor.modal.description"
                    defaultMessage="Edit the item details below. Required fields are marked with an asterisk."
                  />
                </div>

                {isLoading ? (
                  <Loading />
                ) : (
                  <>
                    {template?.templateFields?.items &&
                      Array.isArray(template.templateFields.items) &&
                      template.templateFields.items.length > 0 && (
                        <FormFieldsGenerator
                          key={`item-editor-${itemIndex}`}
                          formValues={template.templateFields.items}
                          controlledVocab={controlledVocab}
                        />
                      )}

                    {/* Display validation errors if any */}
                    {validationErrors && validationErrors.length > 0 && (
                      <div style={{ marginTop: '16px' }}>
                        <Callout type="error">
                          <div>
                            <strong>Validation Errors:</strong>
                            <ul>
                              {validationErrors.map((error, index) => (
                                <li key={index}>{error}</li>
                              ))}
                            </ul>
                          </div>
                        </Callout>
                      </div>
                    )}
                  </>
                )}
              </div>
            </form>
          </Modal>
        );
      }}
    />
  );
};

ItemEditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  itemIndex: PropTypes.number,
  fields: PropTypes.object,
  template: PropTypes.object,
  controlledVocab: PropTypes.object,
  holdingsRecordId: PropTypes.string,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  onClose: PropTypes.func,
  isLoading: PropTypes.bool,
  validationErrors: PropTypes.arrayOf(PropTypes.string),
};

ItemEditModal.defaultProps = {
  itemIndex: null,
  fields: {},
  template: {},
  controlledVocab: {},
  holdingsRecordId: '',
  onSave: () => {},
  onCancel: () => {},
  onClose: () => {},
  isLoading: false,
  validationErrors: [],
};

export default ItemEditModal;
